import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Camera, Save, X, FileSignature, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface FormatEvidence {
  formatId: number;
  formatName: string;
  formatType: string;
  expectedCount: number;
  actualCount: number;
  discrepancyReason?: string;
  notes?: string;
  installationStatus: 'installed' | 'not_installed' | 'partial';
}

interface MediaRequirement {
  id: number;
  name: string;
  type: string;
  quantity: number;
}

interface PackData {
  packId: number;
  packName: string;
  expected: number;
  delivered: number;
  mediaRequirements: MediaRequirement[];
  formatEvidence?: FormatEvidence[];
}

interface VisitData {
  taskId: number;
  visitId?: number;
  storeId: number;
  operatorId: number;
  campaignId?: number;
  storeName: string;
  operatorName: string;
  taskName: string;
  currentStatus: string;
  packs?: PackData[];
  // Legacy support for media requirements format (supports both old and new field names)
  mediaRequirements?: Array<{
    // Old format fields
    id?: number;
    name?: string;
    type?: string;
    quantity?: number;
    expected?: number;
    delivered?: number;
    status?: string;
    // New format fields from LiveAssignment
    formatId?: number;
    formatName?: string;
    formatType?: string;
  }>;
  currentNotes?: string;
  currentSignatureUrl?: string;
  currentDiscrepancy?: string;
}

interface AdminManualSignOffModalProps {
  isOpen: boolean;
  onClose: () => void;
  visitData: VisitData | null;
  onSuccess: () => void;
}

export function AdminManualSignOffModal({
  isOpen,
  onClose,
  visitData,
  onSuccess
}: AdminManualSignOffModalProps) {
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState(visitData?.currentNotes || '');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [expandedPacks, setExpandedPacks] = useState<Set<number>>(new Set());
  
  // Format evidence state - track installation status and discrepancy reasons per format
  const [formatEvidence, setFormatEvidence] = useState<Record<string, FormatEvidence>>({});
  
  const { toast } = useToast();

  // Convert legacy mediaRequirements to pack format for backward compatibility
  const getPacksData = (): PackData[] => {
    console.log('🔍 AdminManualSignOffModal: visitData structure:', visitData);
    
    if (visitData?.packs) {
      console.log('📦 Using new pack format data:', visitData.packs);
      return visitData.packs;
    }
    
    // Convert legacy mediaRequirements to a single pack
    if (visitData?.mediaRequirements) {
      console.log('📄 Converting legacy mediaRequirements:', visitData.mediaRequirements);
      
      // CRITICAL FIX: Use pack quantity (always 1) instead of summing media format quantities
      // Each task represents completing 1 pack, regardless of how many media formats it contains
      const packExpected = 1; // Pack-based system: each task = 1 pack
      const packDelivered = visitData.mediaRequirements.some(req => (req.delivered || 0) > 0) ? 1 : 0;
      
      const convertedPack = {
        packId: visitData.taskId, // Use taskId as fallback pack ID
        packName: visitData.taskName || 'Task Pack',
        expected: packExpected, // Always 1 pack per task
        delivered: packDelivered, // 1 if any evidence exists, 0 otherwise
        mediaRequirements: visitData.mediaRequirements.map(req => ({
          // Map both old and new field names
          id: req.id || req.formatId || 0,
          name: req.name || req.formatName || 'Unknown Format',
          type: req.type || req.formatType || 'unknown',
          quantity: req.quantity || req.expected || 0
        }))
      };
      
      console.log('✅ Converted pack data (FIXED - using pack quantity):', convertedPack);
      return [convertedPack];
    }
    
    console.log('⚠️ No pack or mediaRequirements data found');
    return [];
  };

  // Initialize format evidence from pack data and any existing operator submissions
  useEffect(() => {
    if (visitData) {
      const packsData = getPacksData();
      const evidence: Record<string, FormatEvidence> = {};
      
      packsData.forEach(pack => {
        pack.mediaRequirements.forEach(format => {
          const key = `${pack.packId}-${format.id}`;
          evidence[key] = {
            formatId: format.id,
            formatName: format.name,
            formatType: format.type,
            expectedCount: format.quantity,
            actualCount: format.quantity, // Default to expected - admin can modify
            discrepancyReason: '',
            installationStatus: 'installed' // Default optimistic status
          };
        });
      });
      
      setFormatEvidence(evidence);
      setNotes(visitData.currentNotes || '');
    }
  }, [visitData]);

  // Helper functions for format evidence management
  const updateFormatEvidence = (packId: number, formatId: number, updates: Partial<FormatEvidence>) => {
    const key = `${packId}-${formatId}`;
    setFormatEvidence(prev => ({
      ...prev,
      [key]: { ...prev[key], ...updates }
    }));
  };

  const getInstallationStatusColor = (status: string) => {
    switch (status) {
      case 'installed': return 'text-green-600 bg-green-50 border-green-200';
      case 'partial': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'not_installed': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getFormatEvidence = (packId: number, formatId: number): FormatEvidence | null => {
    const key = `${packId}-${formatId}`;
    return formatEvidence[key] || null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Update actual count for a specific format
  const handleActualCountChange = (packId: number, formatId: number, count: string) => {
    const numericCount = Math.max(0, parseInt(count) || 0);
    const evidence = getFormatEvidence(packId, formatId);
    if (evidence) {
      const newStatus: 'installed' | 'not_installed' | 'partial' = 
        numericCount === 0 ? 'not_installed' :
        numericCount < evidence.expectedCount ? 'partial' : 'installed';
      
      updateFormatEvidence(packId, formatId, {
        actualCount: numericCount,
        installationStatus: newStatus
      });
    }
  };

  const togglePackExpansion = (packId: number) => {
    setExpandedPacks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(packId)) {
        newSet.delete(packId);
      } else {
        newSet.add(packId);
      }
      return newSet;
    });
  };

  const handleUploadFile = async (fileType: 'signature' | 'image') => {
    if (!selectedFile) return null;

    const uploadFormData = new FormData();
    uploadFormData.append('file', selectedFile);
    uploadFormData.append('visitId', visitData?.visitId?.toString() || '0');
    uploadFormData.append('storeId', visitData?.storeId?.toString() || '0');
    uploadFormData.append('operatorId', visitData?.operatorId?.toString() || '0');
    uploadFormData.append('fileType', fileType);

    try {
      const response = await fetch('/api/admin-signoff/upload-admin-evidence', {
        method: 'POST',
        credentials: 'omit',
        body: uploadFormData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const result = await response.json();
      return result.secure_url;
    } catch (error) {
      toast({
        title: "Upload Error",
        description: "Failed to upload file. Please try again.",
        variant: "destructive",
      });
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!visitData) return;
    
    setLoading(true);
    try {
      // Upload file if selected
      let uploadedUrl = visitData.currentSignatureUrl || '';
      if (selectedFile) {
        uploadedUrl = await handleUploadFile('signature');
        if (!uploadedUrl) {
          setLoading(false);
          return;
        }
      }

      // Prepare format evidence data for submission
      const formatEvidenceData = Object.values(formatEvidence);
      const hasAnyDiscrepancies = formatEvidenceData.some(evidence => 
        evidence.installationStatus === 'not_installed' || evidence.installationStatus === 'partial'
      );

      // Check that all non-installed/partial formats have discrepancy reasons
      const missingReasons = formatEvidenceData.filter(evidence => 
        (evidence.installationStatus === 'not_installed' || evidence.installationStatus === 'partial') &&
        !evidence.discrepancyReason?.trim()
      );

      if (missingReasons.length > 0) {
        toast({
          title: "Missing Discrepancy Reasons",
          description: `Please provide reasons for all non-installed or partially installed formats.`,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      // Prepare data for submission with format evidence  
      const submissionData: any = {
        taskId: visitData.taskId,
        visitId: visitData.visitId || 0,
        storeId: visitData.storeId,
        operatorId: visitData.operatorId,
        campaignId: visitData.campaignId,
        signatureUrl: uploadedUrl,
        notes: notes,
        completionStatus: 'admin_complete', // Manual sign-off always completes
        formats: formatEvidenceData.map(evidence => ({
          formatId: evidence.formatId,
          expectedCount: evidence.expectedCount,
          actualCount: evidence.actualCount,
          discrepancyReason: evidence.discrepancyReason,
          notes: evidence.notes
        })), // Backend expects 'formats' array
        hasDiscrepancies: hasAnyDiscrepancies
      };

      // For backwards compatibility, also include legacy format for old API
      const deliveredQuantities: Record<number, number> = {};
      if (visitData?.mediaRequirements) {
        visitData.mediaRequirements.forEach(req => {
          const formatId = req.id || req.formatId || 0;
          if (formatId > 0) {
            const evidence = formatEvidenceData.find(e => e.formatId === formatId);
            deliveredQuantities[formatId] = evidence?.actualCount || 0;
          }
        });
      }
      submissionData.deliveredQuantities = deliveredQuantities;

      // Submit admin sign-off with file upload
      console.log('🚀 Submitting admin sign-off with data:', submissionData);
      let response;
      if (selectedFile) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('adminPhoto', selectedFile);
        
        // Add all submission data as form fields
        Object.entries(submissionData).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            if (typeof value === 'object') {
              formData.append(key, JSON.stringify(value));
            } else {
              formData.append(key, value.toString());
            }
          }
        });

        console.log('📤 Sending FormData submission to /api/admin-signoff/manual-signoff');
        response = await fetch('/api/admin-signoff/manual-signoff', {
          method: 'POST',
          credentials: 'omit',
          body: formData
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ FormData submission failed:', response.status, errorText);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        response = await response.json();
        console.log('✅ FormData submission successful:', response);
      } else {
        // Use regular API request for no file upload
        console.log('📤 Sending JSON submission to /api/admin-signoff/manual-signoff');
        response = await apiRequest('/api/admin-signoff/manual-signoff', 'POST', submissionData);
        console.log('✅ JSON submission successful:', response);
      }

      toast({
        title: "Task Signed Off",
        description: "Admin manual sign-off completed successfully",
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Admin sign-off error:', error);
      toast({
        title: "Sign-Off Failed",
        description: error instanceof Error ? error.message : "Failed to complete sign-off",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5" />
            Admin Manual Sign-Off
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Summary Section at Top */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h3 className="font-semibold mb-3 text-blue-900">Task Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Store:</strong> {visitData?.storeName || 'Unknown'}</p>
                <p><strong>Operator:</strong> {visitData?.operatorName || 'Unknown'}</p>
              </div>
              <div>
                <p><strong>Campaign + Task:</strong> {visitData?.taskName || 'Task'}</p>
                <p><strong>Status:</strong> <span className="text-orange-600 font-medium">Admin Pending Review</span></p>
              </div>
            </div>
          </div>

          {/* Format-Level Installation Breakdown */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Format-Level Installation Status</Label>
            
            {getPacksData().map((pack) => (
              <div key={pack.packId} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Pack Header */}
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-900">{pack.packName}</h4>
                  <p className="text-sm text-gray-600">Review installation status for each format</p>
                </div>
                
                {/* Format Details Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Format Name</th>
                        <th className="px-4 py-2 text-center font-medium text-gray-700">Expected</th>
                        <th className="px-4 py-2 text-center font-medium text-gray-700">Actual</th>
                        <th className="px-4 py-2 text-center font-medium text-gray-700">Status</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Discrepancy Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pack.mediaRequirements.map((format, index) => {
                        const evidence = getFormatEvidence(pack.packId, format.id);
                        const isNotInstalled = evidence?.installationStatus === 'not_installed';
                        const isPartial = evidence?.installationStatus === 'partial';
                        
                        return (
                          <tr key={format.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-3">
                              <div>
                                <p className="font-medium text-gray-900">{format.name}</p>
                                <p className="text-xs text-gray-500 capitalize">{format.type}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="font-semibold text-gray-700">{format.quantity}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Input
                                type="number"
                                min="0"
                                max={format.quantity}
                                value={evidence?.actualCount || 0}
                                onChange={(e) => handleActualCountChange(pack.packId, format.id, e.target.value)}
                                className="w-16 text-center mx-auto"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getInstallationStatusColor(evidence?.installationStatus || 'installed')}`}>
                                {evidence?.installationStatus === 'installed' && '✓ Installed'}
                                {evidence?.installationStatus === 'partial' && '⚠ Partial'}
                                {evidence?.installationStatus === 'not_installed' && '✗ Not Installed'}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {(isNotInstalled || isPartial) ? (
                                <Input
                                  placeholder="Required for non-installed formats"
                                  value={evidence?.discrepancyReason || ''}
                                  onChange={(e) => updateFormatEvidence(pack.packId, format.id, { discrepancyReason: e.target.value })}
                                  className="text-sm"
                                  required
                                />
                              ) : (
                                <span className="text-gray-400 text-sm">N/A</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          {/* Admin Notes and Signature - moved to bottom */}
          <div className="space-y-4 border-t pt-4">
            <Label className="text-base font-semibold">Admin Notes and Evidence</Label>
            
            {/* Admin Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add any additional notes about the sign-off and installation status..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Upload Signature or Evidence (Optional)</Label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              {previewUrl ? (
                <div className="space-y-2">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-w-full max-h-48 object-contain mx-auto rounded"
                  />
                  <div className="flex justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                      }}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">Upload signature or evidence image</p>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="w-full"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Completing...' : 'Complete Sign-Off'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
